// ============================================================================
// COLUMN SELECTOR — toolbar control for showing/hiding grid columns
// ============================================================================
// Mirrors the enterprise sample-template implementation. Renders an icon
// button (grid icon) in the ModuleHeader toolbar; opens a popover with
// per-column checkboxes, Select All / Reset / Cancel / Apply controls.

import React, { useEffect, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';

export interface ColumnDefinition {
  /** Column id (must match the SyutGrid column id) */
  id: string;
  /** Display label in the picker */
  label: string;
  /** Optional grid field (cosmetic; not used for visibility) */
  field?: string;
  /** If true, the column cannot be hidden */
  required?: boolean;
  /** Default visibility (used for the Reset action) */
  defaultVisible: boolean;
}

export interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [isMobile, setIsMobile] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Sync local selection with parent when popover (re)opens
  useEffect(() => {
    if (isOpen) setSelectedColumns(initialVisible);
  }, [isOpen, initialVisible]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find((c) => c.id === columnId);
    if (column?.required) return;
    setSelectedColumns((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId],
    );
  };

  const handleSelectAll = () => {
    const allIds = columns.filter((c) => !c.required).map((c) => c.id);
    const requiredIds = columns.filter((c) => c.required).map((c) => c.id);
    if (selectedColumns.length === columns.length) setSelectedColumns(requiredIds);
    else setSelectedColumns([...requiredIds, ...allIds]);
  };

  const handleApply = () => {
    onApply(selectedColumns);
    setIsOpen(false);
  };
  const handleCancel = () => {
    setSelectedColumns(initialVisible);
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={
          isOpen
            ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' }
            : {}
        }
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <>
          {isMobile && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 999,
              }}
              onClick={() => setIsOpen(false)}
            />
          )}
          <div
            style={{
              position: isMobile ? 'fixed' : 'absolute',
              top: isMobile ? '10%' : 'calc(100% + 0.5rem)',
              left: isMobile ? '50%' : 'auto',
              right: isMobile ? 'auto' : 0,
              transform: isMobile ? 'translateX(-50%)' : 'none',
              width: isMobile ? 'calc(100vw - 2rem)' : '280px',
              maxWidth: '280px',
              maxHeight: '450px',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              border: '2px solid var(--primary)',
              borderRadius: '0.75rem',
              boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '0.75rem 1rem',
                borderBottom: '2px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                borderRadius: '0.75rem 0.75rem 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                Manage Columns
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <IconSystem.x size={16} />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem 0', minHeight: 0 }}>
              {columns.map((column) => (
                <div
                  key={column.id}
                  onClick={() => toggleColumn(column.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.5rem 1rem',
                    cursor: column.required ? 'not-allowed' : 'pointer',
                    gap: '0.625rem',
                    opacity: column.required ? 0.6 : 1,
                  }}
                >
                  <div
                    style={{
                      width: '1rem',
                      height: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid var(--border-color)',
                      borderRadius: '0.25rem',
                      flexShrink: 0,
                      ...(selectedColumns.includes(column.id)
                        ? { background: 'var(--accent-gradient)', borderColor: 'var(--primary)', color: 'white' }
                        : {}),
                    }}
                  >
                    {selectedColumns.includes(column.id) && <IconSystem.check size={12} />}
                  </div>
                  <span
                    style={{
                      flex: 1,
                      fontSize: '0.85rem',
                      color: 'var(--text-primary)',
                      userSelect: 'none',
                    }}
                  >
                    {column.label}
                    {column.required && ' *'}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ padding: '0.75rem', borderTop: '2px solid var(--border-color)', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <button
                  onClick={handleSelectAll}
                  style={{
                    flex: 1,
                    padding: '0.375rem',
                    fontSize: '0.75rem',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                  }}
                >
                  {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
                </button>
                <button
                  onClick={() => setConfirmResetOpen(true)}
                  style={{
                    flex: 1,
                    padding: '0.375rem',
                    fontSize: '0.75rem',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                  }}
                >
                  Reset
                </button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={handleCancel} className="btn btn-secondary">
                  Cancel
                </button>
                <button onClick={handleApply} className="btn btn-primary">
                  Apply
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={() => {
          onReset();
          setConfirmResetOpen(false);
          setIsOpen(false);
        }}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

export default ColumnSelector;
